/**
 * Drops Data Store
 *
 * A centralized store for drop data that persists to localStorage.
 * This provides consistent data across all components and enables
 * link view tracking.
 *
 * Production screens use Supabase RPC hooks in `frontend/src/data`.
 */

// Drop interface matching the database schema
export interface Drop {
  id: string
  name: string
  description?: string
  status: 'active' | 'closed' | 'expired'
  photoCount: number
  contributorCount: number
  viewCount: number
  createdAt: Date
  expiresAt?: Date
  coverUrl?: string
  // Contributor token for shareable links
  contributorToken: string
  // Public gallery settings
  isPublicGalleryEnabled?: boolean
  publicGalleryToken?: string
  // Retention tracking (for free tier - 2 weeks after close)
  retentionExpiresAt?: Date
  // When the drop was closed
  closedAt?: Date
  // Trial drop flag - has Pro features even on free tier
  isTrial?: boolean
  // Organization ID for multi-tenancy isolation
  orgId?: string
  // Customizable thank you message (Pro feature)
  customThankYouMessage?: string
  // Organization tier when drop was created (for compression rules)
  orgTier?: TierType
  // Custom branding (Pro/Business feature)
  branding?: DropBranding
}

// Custom branding settings for a drop
export interface DropBranding {
  primaryColor?: string    // Hex color e.g. "#FF5500"
  accentColor?: string     // Hex color for accents
  logoUrl?: string         // Custom logo URL
  headerText?: string      // Custom header text to replace "Upload Photos"
}

// Free tier retention period: 2 weeks (14 days)
export const FREE_TIER_RETENTION_DAYS = 14

// Tier-based drop limits
export const TIER_DROP_LIMITS = {
  free: 2,
  pro: 10,
  business: 1000,
} as const

export type TierType = keyof typeof TIER_DROP_LIMITS

// Error types for API-like responses
export interface DropLimitError {
  code: 'DROP_LIMIT_REACHED'
  message: string
  currentCount: number
  limit: number
  tier: TierType
}

export interface CreateDropResult {
  success: true
  drop: Drop
}

export interface CreateDropError {
  success: false
  error: DropLimitError
}

// Storage key for localStorage persistence
const DROPS_STORAGE_KEY = 'photodrop_drops'
const VIEW_TRACKING_KEY = 'photodrop_view_tracking'
const UPLOAD_ACTIVITY_KEY = 'photodrop_upload_activity'
const AUTH_STORAGE_KEY = 'photodrop_auth'

// Get current organization ID from auth storage
export function getCurrentOrgId(): string | null {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!stored) return null
  try {
    const data = JSON.parse(stored)
    return data.organization?.id || null
  } catch {
    return null
  }
}

// Check if a drop belongs to the current organization
export function isDropOwnedByCurrentOrg(drop: Drop): boolean {
  const currentOrgId = getCurrentOrgId()
  // If drop has no orgId (legacy drop), it's accessible to all
  if (!drop.orgId) return true
  // If no current org (not logged in), can't access org-specific drops
  if (!currentOrgId) return false
  return drop.orgId === currentOrgId
}

// Upload activity entry interface
export interface UploadActivity {
  dropId: string
  timestamp: number
  photoCount: number
  contributorSessionId: string
}

// Upload activity by drop
interface UploadActivityStore {
  [dropId: string]: UploadActivity[]
}

// Track which sessions have viewed which drops to avoid double-counting
interface ViewTracking {
  [dropToken: string]: {
    lastViewedAt: number
    sessionId: string
  }[]
}

// Generate a session ID for view deduplication
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('photodrop_session_id')
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    sessionStorage.setItem('photodrop_session_id', sessionId)
  }
  return sessionId
}

// Initial drops data
const initialDrops: Drop[] = [
  {
    id: 'drop-1',
    name: "Sarah's Wedding",
    description: 'Photos from the ceremony and reception at the Grand Hotel. Share your favorite moments!',
    status: 'active',
    photoCount: 47,
    contributorCount: 12,
    viewCount: 0,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    expiresAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    contributorToken: 'sarah-wedding-2024',
  },
  {
    id: 'drop-2',
    name: 'Company Retreat 2024',
    description: 'Annual team building event photos - share your best shots!',
    status: 'active',
    photoCount: 156,
    contributorCount: 34,
    viewCount: 0,
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    contributorToken: 'company-retreat-2024',
  },
  {
    id: 'drop-3',
    name: 'Birthday Party',
    description: 'Photos from the birthday celebration',
    status: 'closed',
    photoCount: 23,
    contributorCount: 8,
    viewCount: 0,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    contributorToken: 'birthday-party-123',
  },
]

// Serialize drops to localStorage-friendly format
function serializeDrops(drops: Drop[]): string {
  return JSON.stringify(drops.map(drop => ({
    ...drop,
    createdAt: drop.createdAt.toISOString(),
    expiresAt: drop.expiresAt?.toISOString(),
  })))
}

// Deserialize drops from localStorage
function deserializeDrops(data: string): Drop[] {
  try {
    const parsed = JSON.parse(data)
    return parsed.map((drop: Record<string, unknown>) => ({
      ...drop,
      createdAt: new Date(drop.createdAt as string),
      expiresAt: drop.expiresAt ? new Date(drop.expiresAt as string) : undefined,
    }))
  } catch {
    return []
  }
}

// Get all drops from storage (raw, unfiltered - for internal use)
export function getAllDropsRaw(): Drop[] {
  const stored = localStorage.getItem(DROPS_STORAGE_KEY)
  if (stored) {
    return deserializeDrops(stored)
  }
  // Initialize with default drops
  saveDrops(initialDrops)
  return initialDrops
}

// Get the index of an owned drop in the full drop list
function getOwnedDropIndex(allDrops: Drop[], id: string): number {
  const index = allDrops.findIndex(d => d.id === id)
  if (index === -1) return -1
  if (!isDropOwnedByCurrentOrg(allDrops[index])) return -1
  return index
}

// Get drops filtered by current organization
export function getDrops(): Drop[] {
  const allDrops = getAllDropsRaw()
  // Filter to only show drops belonging to current org (or legacy drops without orgId)
  return allDrops.filter(isDropOwnedByCurrentOrg)
}

// Save drops to storage
export function saveDrops(drops: Drop[]): void {
  localStorage.setItem(DROPS_STORAGE_KEY, serializeDrops(drops))
}

// Access error type for unauthorized access
export interface DropAccessError {
  code: 'NOT_FOUND' | 'ACCESS_DENIED'
  message: string
}

export interface DropAccessResult {
  drop: Drop | null
  error?: DropAccessError
}

// Get a single drop by ID with access control
export function getDropById(id: string): Drop | null {
  const drops = getDrops() // Already filtered by org
  return drops.find(d => d.id === id) || null
}

// Get a single drop by ID with detailed access result (for pages that need to show 403 vs 404)
export function getDropByIdWithAccess(id: string): DropAccessResult {
  const allDrops = getAllDropsRaw()
  const drop = allDrops.find(d => d.id === id)

  if (!drop) {
    return {
      drop: null,
      error: { code: 'NOT_FOUND', message: 'Drop not found' }
    }
  }

  if (!isDropOwnedByCurrentOrg(drop)) {
    return {
      drop: null,
      error: { code: 'ACCESS_DENIED', message: 'You do not have permission to access this drop' }
    }
  }

  return { drop }
}

// Get a drop by contributor token
export function getDropByToken(token: string): Drop | null {
  const allDrops = getAllDropsRaw()
  return allDrops.find(d => d.contributorToken === token) || null
}

// Get a drop by public gallery token (for public gallery access)
export function getDropByGalleryToken(token: string): Drop | null {
  const allDrops = getAllDropsRaw()
  return allDrops.find(d => d.publicGalleryToken === token && d.isPublicGalleryEnabled) || null
}

// Enable public gallery for a drop
export function enablePublicGallery(dropId: string): { token: string } | null {
  const allDrops = getAllDropsRaw()
  const index = getOwnedDropIndex(allDrops, dropId)
  if (index === -1) return null

  // Generate a unique public gallery token if not exists
  if (!allDrops[index].publicGalleryToken) {
    allDrops[index].publicGalleryToken = `gallery-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`
  }
  allDrops[index].isPublicGalleryEnabled = true
  saveDrops(allDrops)

  return { token: allDrops[index].publicGalleryToken! }
}

// Disable public gallery for a drop
export function disablePublicGallery(dropId: string): boolean {
  const allDrops = getAllDropsRaw()
  const index = getOwnedDropIndex(allDrops, dropId)
  if (index === -1) return false

  allDrops[index].isPublicGalleryEnabled = false
  saveDrops(allDrops)
  return true
}

// Count active drops (non-closed, non-expired)
export function getActiveDropCount(): number {
  const drops = getDrops()
  return drops.filter(d => d.status === 'active').length
}

// Check if the user can create a new drop given their tier
export function canCreateDrop(tier: TierType): { allowed: boolean; currentCount: number; limit: number } {
  const currentCount = getActiveDropCount()
  const limit = TIER_DROP_LIMITS[tier]
  return {
    allowed: currentCount < limit,
    currentCount,
    limit,
  }
}

// Create a new drop (with tier limit check)
export function createDrop(drop: Omit<Drop, 'id' | 'viewCount' | 'contributorToken' | 'orgId'>, tier?: TierType): Drop {
  const allDrops = getAllDropsRaw()
  const currentOrgId = getCurrentOrgId()
  const newDrop: Drop = {
    ...drop,
    id: `drop-${Date.now()}`,
    viewCount: 0,
    contributorToken: `${drop.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now().toString(36)}`,
    orgId: currentOrgId || undefined, // Associate with current org
    orgTier: tier, // Store org tier for compression rules
  }
  allDrops.unshift(newDrop)
  saveDrops(allDrops)
  return newDrop
}

// Create a new drop with tier validation (returns result object)
export function createDropWithValidation(
  drop: Omit<Drop, 'id' | 'viewCount' | 'contributorToken'>,
  tier: TierType
): CreateDropResult | CreateDropError {
  const { allowed, currentCount, limit } = canCreateDrop(tier)

  if (!allowed) {
    const tierName = tier.charAt(0).toUpperCase() + tier.slice(1)
    return {
      success: false,
      error: {
        code: 'DROP_LIMIT_REACHED',
        message: `You've reached the maximum of ${limit} active drops on the ${tierName} plan. Close an existing drop or upgrade your plan to create more.`,
        currentCount,
        limit,
        tier,
      },
    }
  }

  const newDrop = createDrop(drop, tier)
  return { success: true, drop: newDrop }
}

// Update a drop
export function updateDrop(id: string, updates: Partial<Drop>): Drop | null {
  const allDrops = getAllDropsRaw()
  const index = getOwnedDropIndex(allDrops, id)
  if (index === -1) return null

  const oldDrop = allDrops[index]
  allDrops[index] = { ...allDrops[index], ...updates }
  saveDrops(allDrops)

  // If the name was updated, update related notifications too
  if (updates.name && updates.name !== oldDrop.name) {
    // Dynamic import to avoid circular dependencies
    import('./notificationsStore').then(({ updateDropNameInNotifications }) => {
      updateDropNameInNotifications(id, oldDrop.name, updates.name!)
    })
  }

  return allDrops[index]
}

// Regenerate the contributor token for a drop (invalidates old links)
export function regenerateContributorToken(id: string): { oldToken: string; newToken: string; drop: Drop } | null {
  const allDrops = getAllDropsRaw()
  const index = getOwnedDropIndex(allDrops, id)
  if (index === -1) return null

  const oldToken = allDrops[index].contributorToken
  const newToken = `${allDrops[index].name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now().toString(36)}`

  allDrops[index].contributorToken = newToken
  saveDrops(allDrops)

  return { oldToken, newToken, drop: allDrops[index] }
}

// Close a drop (with retention tracking for free tier)
export function closeDrop(id: string, isFreeTier: boolean): Drop | null {
  const allDrops = getAllDropsRaw()
  const index = getOwnedDropIndex(allDrops, id)
  if (index === -1) return null

  const now = new Date()
  const closedAt = now

  // For free tier, set retention expiry to 2 weeks after close
  let retentionExpiresAt: Date | undefined
  if (isFreeTier) {
    retentionExpiresAt = new Date(now.getTime() + FREE_TIER_RETENTION_DAYS * 24 * 60 * 60 * 1000)
  }

  allDrops[index] = {
    ...allDrops[index],
    status: 'closed',
    closedAt,
    retentionExpiresAt,
  }
  saveDrops(allDrops)
  return allDrops[index]
}

// Check and clean up expired retention drops
export function cleanupExpiredDrops(): string[] {
  const allDrops = getAllDropsRaw()
  const now = new Date()
  const expiredIds: string[] = []

  const remainingDrops = allDrops.filter(drop => {
    if (
      isDropOwnedByCurrentOrg(drop) &&
      drop.retentionExpiresAt &&
      new Date(drop.retentionExpiresAt) < now
    ) {
      expiredIds.push(drop.id)
      return false
    }
    return true
  })

  if (expiredIds.length > 0) {
    saveDrops(remainingDrops)
  }

  return expiredIds
}

// Get drops that are approaching retention expiry (within 3 days)
export function getDropsNearRetentionExpiry(): Drop[] {
  const drops = getDrops()
  const now = new Date()
  const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000)

  return drops.filter(drop => {
    if (!drop.retentionExpiresAt) return false
    const expiryDate = new Date(drop.retentionExpiresAt)
    return expiryDate > now && expiryDate <= threeDaysFromNow
  })
}

// Delete a drop (and its associated photos)
// Track recently deleted drops to handle React StrictMode double-invocation
const recentlyDeletedDrops = new Set<string>()

export function deleteDrop(id: string): boolean {
  // Check if this drop was already deleted (handles React StrictMode double-call)
  if (recentlyDeletedDrops.has(id)) {
    return true // Already deleted, return success
  }

  // Use getAllDropsRaw() to get all drops, not just org-filtered ones
  // This ensures we don't accidentally delete drops from other orgs
  const allDrops = getAllDropsRaw()
  const index = getOwnedDropIndex(allDrops, id)
  if (index === -1) return false

  const filteredDrops = allDrops.filter(d => d.id !== id)

  saveDrops(filteredDrops)

  // Track this deletion for a short time
  recentlyDeletedDrops.add(id)
  setTimeout(() => recentlyDeletedDrops.delete(id), 1000)

  // Cascade delete: remove all photos associated with this drop
  import('./photosStore').then(({ deletePhotosByDropId }) => {
    const deletedPhotos = deletePhotosByDropId(id)
    if (deletedPhotos > 0) {
      console.log(`Cascade deleted ${deletedPhotos} photos for drop ${id}`)
    }
  })

  return true
}

// Get view tracking data
function getViewTracking(): ViewTracking {
  const stored = localStorage.getItem(VIEW_TRACKING_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      return {}
    }
  }
  return {}
}

// Save view tracking data
function saveViewTracking(tracking: ViewTracking): void {
  localStorage.setItem(VIEW_TRACKING_KEY, JSON.stringify(tracking))
}

// Track a view of a drop's contributor link
// Returns true if this is a new view, false if already counted for this session
export function trackView(token: string): { isNewView: boolean; drop: Drop | null } {
  const drop = getDropByToken(token)
  if (!drop) {
    return { isNewView: false, drop: null }
  }

  const sessionId = getSessionId()
  const tracking = getViewTracking()

  // Check if this session already viewed this drop
  const dropViews = tracking[token] || []
  const alreadyViewed = dropViews.some(v => v.sessionId === sessionId)

  if (alreadyViewed) {
    return { isNewView: false, drop }
  }

  // Record this view
  dropViews.push({
    lastViewedAt: Date.now(),
    sessionId,
  })
  tracking[token] = dropViews
  saveViewTracking(tracking)

  // Increment the view count on the drop
  const allDrops = getAllDropsRaw()
  const dropIndex = allDrops.findIndex(d => d.contributorToken === token)
  if (dropIndex !== -1) {
    allDrops[dropIndex].viewCount += 1
    saveDrops(allDrops)
  }

  return { isNewView: true, drop: allDrops[dropIndex] || drop }
}

// Get total views across all drops
export function getTotalViews(): number {
  const drops = getDrops()
  return drops.reduce((sum, d) => sum + (d.viewCount || 0), 0)
}

// Get total photos across all drops
export function getTotalPhotos(): number {
  const drops = getDrops()
  return drops.reduce((sum, d) => sum + (d.photoCount || 0), 0)
}

// Get total contributors across all drops
export function getTotalContributors(): number {
  const drops = getDrops()
  return drops.reduce((sum, d) => sum + (d.contributorCount || 0), 0)
}

// Clear all data (for testing)
export function clearAllData(): void {
  localStorage.removeItem(DROPS_STORAGE_KEY)
  localStorage.removeItem(VIEW_TRACKING_KEY)
  sessionStorage.removeItem('photodrop_session_id')
}

// Reset to initial data (for testing)
export function resetToInitialData(): void {
  clearAllData()
  saveDrops(initialDrops)
}

// ==================== UPLOAD ACTIVITY TRACKING ====================

// Get upload activity store
function getUploadActivityStore(): UploadActivityStore {
  const stored = localStorage.getItem(UPLOAD_ACTIVITY_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      return {}
    }
  }
  return {}
}

// Save upload activity store
function saveUploadActivityStore(store: UploadActivityStore): void {
  localStorage.setItem(UPLOAD_ACTIVITY_KEY, JSON.stringify(store))
}

// Record an upload activity event
export function recordUploadActivity(dropId: string, photoCount: number): UploadActivity {
  const sessionId = getSessionId()
  const store = getUploadActivityStore()

  const activity: UploadActivity = {
    dropId,
    timestamp: Date.now(),
    photoCount,
    contributorSessionId: sessionId,
  }

  if (!store[dropId]) {
    store[dropId] = []
  }
  store[dropId].push(activity)
  saveUploadActivityStore(store)

  // Also update the drop's photo count
  const allDrops = getAllDropsRaw()
  const dropIndex = allDrops.findIndex(d => d.id === dropId)
  if (dropIndex !== -1) {
    allDrops[dropIndex].photoCount += photoCount
    saveDrops(allDrops)
  }

  return activity
}

// Get upload activity for a specific drop
export function getUploadActivityByDrop(dropId: string): UploadActivity[] {
  const store = getUploadActivityStore()
  return store[dropId] || []
}

// Get upload activity grouped by time period (for charts)
export interface ActivityByPeriod {
  period: string
  timestamp: number
  uploads: number
  photos: number
}

export function getUploadActivityByPeriod(
  dropId: string,
  groupBy: 'hour' | 'day' | 'week' = 'day'
): ActivityByPeriod[] {
  const activities = getUploadActivityByDrop(dropId)

  if (activities.length === 0) {
    return []
  }

  // Group activities by period
  const grouped: Record<string, { uploads: number; photos: number; timestamp: number }> = {}

  activities.forEach(activity => {
    const date = new Date(activity.timestamp)
    let periodKey: string
    let periodTimestamp: number

    if (groupBy === 'hour') {
      date.setMinutes(0, 0, 0)
      periodTimestamp = date.getTime()
      periodKey = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
      })
    } else if (groupBy === 'day') {
      date.setHours(0, 0, 0, 0)
      periodTimestamp = date.getTime()
      periodKey = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    } else {
      // Week - get start of week (Sunday)
      const dayOfWeek = date.getDay()
      date.setDate(date.getDate() - dayOfWeek)
      date.setHours(0, 0, 0, 0)
      periodTimestamp = date.getTime()
      periodKey = `Week of ${date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })}`
    }

    if (!grouped[periodKey]) {
      grouped[periodKey] = { uploads: 0, photos: 0, timestamp: periodTimestamp }
    }
    grouped[periodKey].uploads += 1
    grouped[periodKey].photos += activity.photoCount
  })

  // Convert to array and sort by timestamp
  return Object.entries(grouped)
    .map(([period, data]) => ({
      period,
      timestamp: data.timestamp,
      uploads: data.uploads,
      photos: data.photos,
    }))
    .sort((a, b) => a.timestamp - b.timestamp)
}

// Get total upload statistics for a drop
export interface UploadStats {
  totalUploads: number
  totalPhotos: number
  uniqueContributors: number
  firstUpload: Date | null
  lastUpload: Date | null
  averagePhotosPerUpload: number
}

export function getUploadStats(dropId: string): UploadStats {
  const activities = getUploadActivityByDrop(dropId)

  if (activities.length === 0) {
    return {
      totalUploads: 0,
      totalPhotos: 0,
      uniqueContributors: 0,
      firstUpload: null,
      lastUpload: null,
      averagePhotosPerUpload: 0,
    }
  }

  const totalPhotos = activities.reduce((sum, a) => sum + a.photoCount, 0)
  const uniqueContributors = new Set(activities.map(a => a.contributorSessionId)).size
  const timestamps = activities.map(a => a.timestamp)

  return {
    totalUploads: activities.length,
    totalPhotos,
    uniqueContributors,
    firstUpload: new Date(Math.min(...timestamps)),
    lastUpload: new Date(Math.max(...timestamps)),
    averagePhotosPerUpload: Math.round((totalPhotos / activities.length) * 10) / 10,
  }
}

// Get contributor data with upload counts for a drop
export interface ContributorData {
  sessionId: string
  displayName: string
  photoCount: number
  uploadCount: number
  firstUpload: Date
  lastUpload: Date
}

export function getContributorsByDrop(dropId: string): ContributorData[] {
  const activities = getUploadActivityByDrop(dropId)

  if (activities.length === 0) {
    return []
  }

  // Group activities by contributor session
  const contributorMap: Record<string, {
    photoCount: number
    uploadCount: number
    timestamps: number[]
  }> = {}

  activities.forEach(activity => {
    const sessionId = activity.contributorSessionId
    if (!contributorMap[sessionId]) {
      contributorMap[sessionId] = {
        photoCount: 0,
        uploadCount: 0,
        timestamps: [],
      }
    }
    contributorMap[sessionId].photoCount += activity.photoCount
    contributorMap[sessionId].uploadCount += 1
    contributorMap[sessionId].timestamps.push(activity.timestamp)
  })

  // Convert to array and sort by most recent upload
  return Object.entries(contributorMap)
    .map(([sessionId, data], index) => ({
      sessionId,
      // Generate a friendly display name based on order
      displayName: `Contributor ${index + 1}`,
      photoCount: data.photoCount,
      uploadCount: data.uploadCount,
      firstUpload: new Date(Math.min(...data.timestamps)),
      lastUpload: new Date(Math.max(...data.timestamps)),
    }))
    .sort((a, b) => b.lastUpload.getTime() - a.lastUpload.getTime())
}

// Clear upload activity (for testing)
export function clearUploadActivity(): void {
  localStorage.removeItem(UPLOAD_ACTIVITY_KEY)
}

// Get recent activity across all drops for activity feed
export interface RecentActivity {
  dropId: string
  dropName: string
  timestamp: number
  photoCount: number
  type: 'upload'
}

export function getRecentActivity(limit: number = 10): RecentActivity[] {
  const store = getUploadActivityStore()
  const drops = getDrops()

  // Create a map of drop names
  const dropNameMap: Record<string, string> = {}
  drops.forEach(drop => {
    dropNameMap[drop.id] = drop.name
  })

  // Collect all activities with drop info
  const allActivities: RecentActivity[] = []

  Object.entries(store).forEach(([dropId, activities]) => {
    activities.forEach(activity => {
      allActivities.push({
        dropId,
        dropName: dropNameMap[dropId] || 'Unknown Drop',
        timestamp: activity.timestamp,
        photoCount: activity.photoCount,
        type: 'upload',
      })
    })
  })

  // Sort by timestamp descending and take limit
  return allActivities
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, limit)
}
